import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface AudioPlayerProps {
  src: string;
  onClose?: () => void;
  /**
   * Start playing as soon as the element is ready.
   *
   * Set by the callers that render this BECAUSE somebody pressed play: without
   * it, pressing Play merely revealed the player and the reader had to press
   * play a second time, on a different control, to hear anything.
   *
   * Off by default — a player that appears on its own must not also start
   * talking, and browsers refuse autoplay without a user gesture anyway.
   */
  autoPlay?: boolean;
  /**
   * Receives the underlying <audio> element, so a caller (the transcript panel) can seek it.
   * Optional; unset callers behave exactly as before.
   */
  elementRef?: React.MutableRefObject<HTMLAudioElement | null>;
}

/** Offered speeds. 1x first so the default is the obvious one. */
const SPEEDS = [1, 1.25, 1.5, 1.75, 2, 0.75, 0.5];

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, onClose, autoPlay = false, elementRef }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!elementRef) return;
    elementRef.current = audioRef.current;
    return () => {
      elementRef.current = null;
    };
  }, [elementRef, src]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleLoadedMetadata = () => setDuration(audio.duration);
    // Driven by the element, never assumed. play() can be refused — by autoplay
    // policy, or because the signed URL expired — and the old code flipped the
    // icon to Pause regardless, so a player that was silent looked like it was
    // playing.
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    // No optimistic setIsPlaying here: the play/pause listeners above report
    // what actually happened, including a refused play().
    if (isPlaying) audio.pause();
    else audio.play().catch(() => { /* the icon stays on Play, which is the truth */ });
  };

  /** Start on mount when the caller opened this because play was pressed. */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !autoPlay) return;
    audio.play().catch(() => { /* refused — the reader can press play */ });
  }, [autoPlay, src]);

  /** Kept on the element, and re-applied when the source changes. */
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = rate;
  }, [rate, src]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3 sm:p-4
                    border border-gray-200 dark:border-gray-700">
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* WRAPS, and the seekbar takes the second line on a phone.
          This was one non-wrapping row of flex-shrink-0 parts: a 40px button, an
          80px clock, a speed select and a 20px+80px volume pair, four gap-4
          gutters and p-4 — about 370px of hard minimum before the seekbar got a
          single pixel. Inside the recording dialog on a 375px phone there is
          roughly 310px, so the player simply hung off the side of the screen.

          Below sm the seekbar goes full-width on its own line (order-last), and
          the controls that fit sit above it. The first line then needs ~212px. */}
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-600 text-white rounded-full hover:bg-green-700 transition"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>

        {/* Time Display */}
        <div className="flex-shrink-0 text-xs sm:text-sm text-gray-600 dark:text-gray-300
                        font-mono tabular-nums min-w-[72px] sm:min-w-[80px]">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Seekbar. basis-full + order-last puts it on its own row below sm;
            from sm it returns to the middle of the single row. */}
        <div className="flex-1 basis-full order-last min-w-[8rem] sm:basis-auto sm:order-none">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            aria-label="Seek"
            className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
            style={{
              // duration is 0 until metadata loads; (t/0)*100 is NaN and the
              // whole gradient is dropped, leaving an unstyled bar.
              background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${duration ? (currentTime / duration) * 100 : 0}%, var(--border) ${duration ? (currentTime / duration) * 100 : 0}%, var(--border) 100%)`
            }}
          />
        </div>

        {/* Speed. A two-minute call at 1x is two minutes of somebody's day;
            reviewers listen at 1.5x and above almost always. */}
        <select
          aria-label="Playback speed"
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          className="flex-shrink-0 text-xs font-mono px-1.5 py-1 rounded border
                     border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 cursor-pointer"
        >
          {SPEEDS.map((r) => <option key={r} value={r}>{r}x</option>)}
        </select>

        {/* Volume Control */}
        {/* The slider is sm-and-up only. It is 80px — not a finger-sized
            control — and a phone already has volume keys, so on a phone it cost
            width the seekbar needed and gave nothing back. Mute stays: that one
            has no hardware equivalent in a quiet room. */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="Volume"
            className="hidden sm:block w-20 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg
                       appearance-none cursor-pointer slider"
          />
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--accent);
          cursor: pointer;
        }
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--accent);
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default AudioPlayer;
